/**
 * Lazy skin loader factory.
 *
 * Boston Bricks (and future asset-heavy skins) reference large 9-slice PNG
 * assets via import.meta.url. Statically importing these modules causes Vite
 * to pre-bundle the PNGs, which hangs vitest. This factory centralises the
 * dynamic-import workaround so every component doesn't duplicate it.
 *
 * Path construction is centralised here so that callers don't need to know
 * the relative path from their own file to the shared skins directory.
 *
 * Usage:
 *   import { createSkinLoader } from '../../shared/skin-loader.js';
 *   const _bbSkin = createSkinLoader('boston-bricks', 'button');
 *
 *   // In updated():
 *   if (_bbSkin.loaded) {
 *     this.shadowRoot.adoptedStyleSheets = [_bbSkin.styles];
 *     _bbSkin.module.render(this);
 *   } else {
 *     _bbSkin.load().then(() => this.requestUpdate());
 *   }
 */

// Resolve from this module's location to design-system/skins/
// shared/skin-loader.js → ../design-system/skins/
const _skinsBase = ['..', 'design-system', 'skins'].join('/');

/**
 * @param {string} skinName   e.g. 'boston-bricks'
 * @param {string} component  e.g. 'button', 'input'
 */
/**
 * Dynamic-import a skin module by (skinName, component), reusing the same
 * non-statically-analysable path construction + `@vite-ignore` as
 * createSkinLoader so Vite does not pre-bundle asset-heavy skins (which hangs
 * vitest). Returns the raw module promise; the skin-resolution mixin
 * normalises the `{ styles, render }` shape. Use this as a `skinLoaders`
 * registry value for asset skins:
 *
 *   static skinLoaders = {
 *     'boston-bricks': () => loadSkinModule('boston-bricks', 'button'),
 *   };
 *
 * @param {string} skinName   e.g. 'boston-bricks'
 * @param {string} component  e.g. 'button', 'input'
 * @returns {Promise<any>}
 */
export function loadSkinModule(skinName, component) {
  const path = [_skinsBase, skinName, `${component}-skin.js`].join('/');
  return import(/* @vite-ignore */ path);
}

export function createSkinLoader(skinName, component) {
  const path = [_skinsBase, skinName, `${component}-skin.js`].join('/');
  let mod = null;
  let sheet = null;

  return {
    get loaded() { return !!mod; },
    get styles() { return sheet; },
    get module() { return mod; },
    // C+ rendering tier (cplus-rendering-tier-contract-addendum-v1 Rec 5):
    // expose the skin's meta so the host dispatches on renderingTier rather
    // than hardcoded skin-name branching. null until loaded / for CSS skins
    // that declare no meta (tier defaults to 'css').
    get meta() { return mod ? (mod.meta ?? null) : null; },
    async load() {
      if (mod) return;
      const imported = await import(/* @vite-ignore */ path);
      if (typeof imported.styles !== 'string') {
        throw new Error(
          `Skin module "${path}" does not export a "styles" string`
        );
      }
      const s = new CSSStyleSheet();
      s.replaceSync(imported.styles);
      // Commit only after everything succeeds — a failed load is retryable
      mod = imported;
      sheet = s;
    }
  };
}
